import {
	Drawer,
	type DrawerProps,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import type { FC, PropsWithChildren, SyntheticEvent } from "react";
import { useRef } from "react";
import { CrossIcon } from "web_myplann/Shared/Icons";

export type MyPlannDrawerProps = DrawerProps & {
	title?: string;
	closeButton?: boolean;
	dissableBackdropClick?: boolean;
};

export const MyPlannDrawer: FC<PropsWithChildren<MyPlannDrawerProps>> = ({
	anchor = "bottom",
	title,
	closeButton,
	dissableBackdropClick,
	children,
	onClose,
	...rest
}) => {
	const startYRef = useRef<number | null>(null);
	const closedRef = useRef(false);
	const threshold = 60;

	const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		startYRef.current =
			e.touches?.[0]?.clientY ?? e.targetTouches?.[0]?.clientY ?? null;
		e.stopPropagation();
	};

	const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		if (anchor === "bottom" && startYRef.current != null) {
			const y =
				e.touches?.[0]?.clientY ?? e.targetTouches?.[0]?.clientY ?? null;
			if (
				y != null &&
				y - startYRef.current > threshold &&
				!closedRef.current
			) {
				closedRef.current = true;
				onClose?.({}, "escapeKeyDown");
			}
		}
		e.stopPropagation();
	};

	const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
		startYRef.current = null;
		closedRef.current = false;
		e.stopPropagation();
	};

	return (
		<Drawer
			anchor={anchor}
			ModalProps={{
				sx: { zIndex: (t) => t.zIndex.drawer + 1 },
				slotProps: { backdrop: { onClick: (e) => e.stopPropagation() } },
			}}
			slotProps={{
				paper: {
					sx: {
						paddingBottom: "min(env(safe-area-inset-bottom, 0px), 10px)",
					},
					onClick: (e: SyntheticEvent) => {
						e.stopPropagation();
					},
				},
			}}
			onClose={(event, reason) => {
				if (reason === "backdropClick" && closeButton) {
					return;
				}
				if (onClose) onClose(event, reason);
			}}
			{...rest}
		>
			<Stack
				pt={2.4}
				pb={1.6}
				px={1.6}
				direction={"row"}
				alignItems={"center"}
				justifyContent={"space-between"}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				sx={{ touchAction: "none" }}
			>
				<Typography
					display="flex"
					alignItems="flex-start"
					fontWeight={600}
					fontSize={16}
				>
					{title}
				</Typography>

				{closeButton && (
					<IconButton
						onClick={(e) => {
							onClose?.(e, "escapeKeyDown");
						}}
					>
						<CrossIcon />
					</IconButton>
				)}
			</Stack>
			<Stack
				sx={{
					flex: "1 1 auto",
					overflowY: "auto",
				}}
			>
				{children}
			</Stack>
		</Drawer>
	);
};
